import React from 'react';
import classNames from 'classnames';
import { CommonProps } from 'src/components/helpers/props';
import { NavLink, useLocation } from 'react-router-dom';

import './index.scss';
import {
  ChevronRightIcon,
  DropdownItem,
  DropdownList,
  ChevronUpIcon,
  ChevronDownIcon,
  Grid,
  GridItem,
} from 'fasoo-ui-component-next';
import { RouteTable } from 'src/shared/utils/routeUtils';
import { isAllowedMenu, userAllowedMenus, userDisabledRequest } from 'src/shared/utils/menuUtils';
import useAuthStore from 'src/hooks/auth/useAuthStore';
import { useTranslation } from 'react-i18next';
import useUserRuleStore from 'src/hooks/user/useUserRuleStore';
import useSettingStore from 'src/hooks/useSettingStore';
import useComponentVisible from 'src/hooks/useComponentVisible';

export interface ContentHeaderProps extends CommonProps {
  routeTable: RouteTable;
}

const ContentHeader: React.FC<CommonProps> = ({ classes, children }) => {
  return <div className={classNames('content-header-root', classes)}>{children}</div>;
};

/*
 * 현재 접속 url을 기준으로 자동으로 만들어 준다.
 */
export interface BreadcrumbProps extends CommonProps {
  routeTable: RouteTable;
}

export const Breadcrumb: React.FC<BreadcrumbProps> = ({ classes, routeTable }) => {
  const { t } = useTranslation(['menu', 'commonsMenu']);

  const depth2RouteTables: RouteTable[] = [];
  if (routeTable.parents.length >= 2 && routeTable.parents[1].menu) {
    depth2RouteTables.push(...routeTable.parents[1].subs.filter((rt) => rt.menu));
  }

  // const back = !routeTable.menu ? _.findLast(routeTable.parents, (prt) => prt.menu === true) : null;

  // console.log('[breadcrumb]: ', routeTable);

  return (
    <div className={classNames('content-header-breadcrumb-root', classes)}>
      <Grid spacing={0}>
        <GridItem xs={12}>
          <ol className="content-header-breadcrumbs">
            {routeTable.parents.map((location, index) => {
              if (index !== 0)
                return (
                  <React.Fragment key={index}>
                    <li>
                      <NavLink to={location.fullPath}>{t(location.title)}</NavLink>
                    </li>
                    {index !== routeTable.parents.length - 1 && (
                      <li className="sep text text-gray-2">
                        <ChevronRightIcon size="xs" />
                      </li>
                    )}
                  </React.Fragment>
                );
              else return null;
            })}
          </ol>
        </GridItem>
      </Grid>
    </div>
  );
};
Breadcrumb.displayName = 'Breadcrumb';

interface HeaderTabItemProps extends CommonProps {
  depth2: RouteTable;
  userAllowedMenus: string[];
  disabledMenus: string[];
}

const HeaderTabItem: React.FC<HeaderTabItemProps> = ({
  classes,
  depth2,
  userAllowedMenus,
  disabledMenus,
}) => {
  const { t } = useTranslation(['userMenu', 'commonsMenu']);
  const { pathname } = useLocation();
  const {
    ref,
    isComponentVisible: open,
    setIsComponentVisible: setOpen,
  } = useComponentVisible(false);

  const activeDepth3List = depth2.subs.filter(
    ({ hidden, menu, adminScopes }) =>
      !hidden && menu && isAllowedMenu(adminScopes, userAllowedMenus)
  );

  const hasSubs = activeDepth3List.length > 0;

  const onClick = (): void => {
    setOpen(!open);
  };

  const activeSubMenus = (): boolean => {
    if (
      activeDepth3List.map((depth3) => depth3.fullPath).includes(pathname) ||
      depth2.fullPath === pathname
    ) {
      return true;
    }

    return false;
  };

  return (
    <li className={hasSubs ? 'submenu-root' : ''}>
      {!hasSubs ? (
        <NavLink to={depth2.fullPath}>
          {t(depth2.title)}
          {activeDepth3List.length > 0 && (open ? <ChevronUpIcon /> : <ChevronDownIcon />)}
        </NavLink>
      ) : (
        // eslint-disable-next-line jsx-a11y/anchor-is-valid
        <a
          title={t(depth2.title)}
          className={activeSubMenus() ? 'active' : ''}
          onClick={onClick}
          ref={ref}
        >
          {t(depth2.title)}
          {open ? <ChevronUpIcon /> : <ChevronDownIcon />}
          <div style={{ position: 'relative' }}>
            <DropdownList on={open} align={'left'}>
              {activeDepth3List.map((depth3, j) => (
                <DropdownItem key={`depth3_${j}`}>
                  <NavLink title={t(depth3.title)} to={depth3.fullPath}>
                    {t(depth3.title)}
                  </NavLink>
                </DropdownItem>
              ))}
            </DropdownList>
          </div>
        </a>
      )}
    </li>
  );
};

/*
 * 현재 접속 url을 기준으로 자동으로 만들어 준다.
 */
export const ContentHeaderTab: React.FC<ContentHeaderProps> = ({ classes, routeTable }) => {
  const { authEntity } = useAuthStore();
  const { initialized: userRuleInitialized, userRuleEntity } = useUserRuleStore();
  const { initialized: settingInitialized, settingEntities } = useSettingStore();

  if (!authEntity) {
    return null;
  }

  if (import.meta.env.VITE_APP_TYPE === 'user' && !userRuleInitialized && !settingInitialized) {
    return null;
  }

  let userAllowedMenu: string[];
  let disabledMenus: string[];
  if (import.meta.env.VITE_APP_TYPE === 'user') {
    userAllowedMenu = userAllowedMenus(userRuleEntity, settingEntities.proxy.enabled);
    disabledMenus = userDisabledRequest(userRuleEntity);
  } else {
    userAllowedMenu = authEntity.menus;
    disabledMenus = authEntity.disabledMenus;
  }

  const depth2RouteTables: RouteTable[] = [];
  if (routeTable.parents.length >= 2 && routeTable.parents[1].menu) {
    depth2RouteTables.push(...routeTable.parents[1].subs.filter((rt) => rt.menu));
  }

  if (!authEntity) {
    return null;
  }
  if (depth2RouteTables.length === 0) {
    return null;
  }

  return (
    <>
      <div className={classNames('content-header-tab', classes)}>
        <ul>
          {depth2RouteTables.map((depth2, i) => {
            return (
              isAllowedMenu(depth2.adminScopes, authEntity.menus) && (
                <HeaderTabItem
                  key={'headerTabItem' + i}
                  depth2={depth2}
                  userAllowedMenus={userAllowedMenu}
                  disabledMenus={disabledMenus}
                />
              )
            );
          })}
        </ul>
      </div>
      <div className="content-header-tab-divider" />
    </>
  );
};
ContentHeaderTab.displayName = 'ContentHeaderTab';

export const ContentTitle: React.FC<CommonProps> = ({ classes, children }) => {
  return <div className={classNames('content-header-title', classes)}>{children}</div>;
};

ContentHeader.displayName = 'ContentHeader';
export default ContentHeader;
